import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { html, nothing } from 'lit';

const CreateUserForm = stateful((props: { onCreated: (userId: string) => void; onCancel: () => void }) => {
  const [initialDocument] = useState(new AdornisUser({}));
  const [isCreating, setIsCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { document, field, validate } = useForm<AdornisUser>(initialDocument, {
    validator: {
      username: nonOptional(),
    },
  });

  // Create is performed inside the dialog so server-side validation failures (e.g. an email
  // that is already in use) can be shown inline without closing the dialog.
  const handleCreate = async () => {
    const error = await validate();
    if (error) return;

    setErrorMessage(null);
    setIsCreating(true);
    try {
      const newUserId = await document.create();
      if (newUserId) props.onCreated(newUserId);
    } catch (error: unknown) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to create user.');
    } finally {
      setIsCreating(false);
    }
  };

  return html`
    <div class="flex flex-col gap-4 min-w-80">
      <h3 class="text-lg font-bold">Create new User</h3>

      <label class="form-control w-full">
        <div class="label"><span class="label-text">Username *</span></div>
        ${TextInput({ ...field('username'), placeholder: 'e.g. jane.doe' })}
      </label>

      <label class="form-control w-full">
        <div class="label"><span class="label-text">Email</span></div>
        ${TextInput({ ...field('email'), placeholder: 'e.g. jane@example.com', type: 'email' })}
      </label>

      ${errorMessage ? html`<div class="alert alert-error text-sm">${errorMessage}</div>` : nothing}

      <div class="flex justify-end gap-2 mt-2">
        <button class="btn btn-ghost" ?disabled=${isCreating} @click=${props.onCancel}>Cancel</button>
        <button class="btn btn-primary" ?disabled=${isCreating} @click=${handleCreate}>
          ${isCreating ? html`<span class="loading loading-spinner loading-sm"></span>` : nothing} Create
        </button>
      </div>
    </div>
  `;
});

/**
 * Shows a dialog to create a new user with username and email fields.
 * Returns the new user's ID if created, or null if cancelled.
 */
export async function showCreateUserDialog(): Promise<string | null> {
  const newUserId = await showDialog<string | null>({
    content: (ok, cancel) =>
      CreateUserForm({
        onCreated: ok,
        onCancel: () => cancel(),
      }),
  }).catch(() => null);

  return newUserId ?? null;
}
